import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import SkalaLogo from "../components/SkalaLogo";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { trackGa4SignUp } from "../lib/ga4";
import { getClickIdsFromContext, getGaClientId } from "../lib/ga4";

export default function SignupPage() {
    const navigate = useNavigate();
    const { register } = useAuth();
    const { language } = useLanguage();
    const [form, setForm] = useState({
        full_name: "",
        monthly_spend: "",
        email: "",
        password: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const copy = {
        en: {
            spendOptions: ["Under $10,000", "$10,000 - $25,000", "$25,000 - $100,000", "$100,000 - $500,000", "$500,000 - $1,000,000", "Over $1,000,000"],
            fillRequired: "Please fill all required fields.",
            passMin: "Password must be at least 6 characters.",
            success: "Account created. Welcome to Adrise.",
            failed: "Signup failed.",
            back: "Back to site",
            heroTitle1: "Create your free account.",
            heroTitle2: "Operate with stability.",
            heroBullets: ["Free — no card, no monthly subscription", "Panel access is live the second you sign up", "Fund with USDT / USDC, request accounts on 7 platforms", "Fee starts at 4% and drops with monthly volume", "24/7 WhatsApp & Telegram operations support"],
            heroFoot: "Free forever · No credit card required",
            title: "Create free account",
            approved: "Already have an account?",
            login: "Sign in",
            fullName: "Full name *",
            company: "Company name",
            website: "Company website",
            monthly: "Monthly ad spend",
            selectRange: "Select range",
            email: "Email *",
            password: "Password *",
            submit: "Create Free Account",
            submitting: "Submitting…",
            review: "Account access is activated immediately after registration.",
            phName: "John Doe", phCompany: "Brand Inc.", phSite: "brand.com", phEmail: "team@brand.com", phPass: "At least 6 characters",
        },
        pt: {
            spendOptions: ["Abaixo de $10.000", "$10.000 - $25.000", "$25.000 - $100.000", "$100.000 - $500.000", "$500.000 - $1.000.000", "Acima de $1.000.000"],
            fillRequired: "Preencha os campos obrigatórios.",
            passMin: "A senha deve ter pelo menos 6 caracteres.",
            success: "Conta criada. Bem-vindo à Adrise.",
            failed: "Falha no cadastro.",
            back: "Voltar ao site",
            heroTitle1: "Crie sua conta grátis.",
            heroTitle2: "Opere com estabilidade.",
            heroBullets: ["Grátis — sem cartão, sem assinatura mensal", "Acesso ao painel liberado no mesmo instante", "Abasteça com USDT / USDC e solicite contas em 7 plataformas", "Fee começa em 4% e cai com o volume mensal", "Suporte 24/7 via WhatsApp & Telegram"],
            heroFoot: "Grátis para sempre · Sem cartão de crédito",
            title: "Criar conta grátis",
            approved: "Já tem uma conta?",
            login: "Entrar",
            fullName: "Nome completo *",
            company: "Nome da empresa",
            website: "Site da empresa",
            monthly: "Investimento mensal em anúncios",
            selectRange: "Selecione a faixa",
            email: "E-mail *",
            password: "Senha *",
            submit: "Criar Conta Grátis",
            submitting: "Enviando…",
            review: "O acesso é ativado imediatamente após o cadastro.",
            phName: "João Silva", phCompany: "Marca Ltda.", phSite: "marca.com", phEmail: "time@marca.com", phPass: "Mínimo de 6 caracteres",
        },
        es: {
            spendOptions: ["Menos de $10.000", "$10.000 - $25.000", "$25.000 - $100.000", "$100.000 - $500.000", "$500.000 - $1.000.000", "Más de $1.000.000"],
            fillRequired: "Completa los campos obligatorios.",
            passMin: "La contraseña debe tener al menos 6 caracteres.",
            success: "Cuenta creada. Bienvenido a Adrise.",
            failed: "Error de registro.",
            back: "Volver al sitio",
            heroTitle1: "Crea tu cuenta gratis.",
            heroTitle2: "Opera con estabilidad.",
            heroBullets: ["Gratis — sin tarjeta, sin suscripción mensual", "Acceso al panel activo al instante", "Fondea con USDT / USDC y solicita cuentas en 7 plataformas", "Fee desde 4% y baja con el volumen mensual", "Soporte 24/7 por WhatsApp & Telegram"],
            heroFoot: "Gratis para siempre · Sin tarjeta de crédito",
            title: "Crear cuenta gratis",
            approved: "¿Ya tienes cuenta?",
            login: "Iniciar sesión",
            fullName: "Nombre completo *",
            company: "Nombre de empresa",
            website: "Sitio web",
            monthly: "Inversión mensual en anuncios",
            selectRange: "Selecciona rango",
            email: "Correo *",
            password: "Contraseña *",
            submit: "Crear Cuenta Gratis",
            submitting: "Enviando…",
            review: "El acceso se activa inmediatamente después del registro.",
            phName: "Juan Pérez", phCompany: "Marca S.A.", phSite: "marca.com", phEmail: "equipo@marca.com", phPass: "Mínimo 6 caracteres",
        },
        ru: {
            spendOptions: ["До $10,000", "$10,000 - $25,000", "$25,000 - $100,000", "$100,000 - $500,000", "$500,000 - $1,000,000", "Более $1,000,000"],
            fillRequired: "Заполните обязательные поля.",
            passMin: "Пароль должен быть не менее 6 символов.",
            success: "Аккаунт создан. Добро пожаловать в Adrise.",
            failed: "Ошибка регистрации.",
            back: "Вернуться на сайт",
            heroTitle1: "Создайте бесплатный аккаунт.",
            heroTitle2: "Работайте стабильно.",
            heroBullets: ["Бесплатно — без карты и без ежемесячной подписки", "Доступ к панели открывается сразу после регистрации", "Пополнение USDT / USDC, аккаунты на 7 платформах", "Комиссия от 4% и снижается с ростом объёма", "Поддержка 24/7 в WhatsApp & Telegram"],
            heroFoot: "Бесплатно навсегда · Карта не нужна",
            title: "Создать бесплатный аккаунт",
            approved: "Уже есть аккаунт?",
            login: "Войти",
            fullName: "Имя и фамилия *",
            company: "Название компании",
            website: "Сайт компании",
            monthly: "Месячный рекламный бюджет",
            selectRange: "Выберите диапазон",
            email: "E-mail *",
            password: "Пароль *",
            submit: "Создать аккаунт",
            submitting: "Отправка…",
            review: "Доступ активируется сразу после регистрации.",
            phName: "Иван Иванов", phCompany: "Brand LLC", phSite: "brand.com", phEmail: "team@brand.com", phPass: "Минимум 6 символов",
        },
        tr: {
            spendOptions: ["$10.000 altı", "$10.000 - $25.000", "$25.000 - $100.000", "$100.000 - $500.000", "$500.000 - $1.000.000", "$1.000.000 üstü"],
            fillRequired: "Lütfen zorunlu alanları doldurun.",
            passMin: "Şifre en az 6 karakter olmalı.",
            success: "Hesabın oluşturuldu. Adrise'a hoş geldin.",
            failed: "Kayıt başarısız.",
            back: "Siteye dön",
            heroTitle1: "Ücretsiz hesabını oluştur.",
            heroTitle2: "Stabil operasyona geç.",
            heroBullets: ["Ücretsiz — kart yok, aylık abonelik yok", "Panel erişimi kayıt anında açılır", "USDT / USDC ile yükle, 7 platformda hesap talep et", "Komisyon %4’ten başlar, aylık hacimle düşer", "7/24 WhatsApp & Telegram operasyon desteği"],
            heroFoot: "Sonsuza kadar ücretsiz · Kredi kartı gerekmez",
            title: "Ücretsiz hesap oluştur",
            approved: "Zaten hesabın var mı?",
            login: "Giriş yap",
            fullName: "Ad soyad *",
            company: "Şirket adı",
            website: "Şirket web sitesi",
            monthly: "Aylık reklam harcaması",
            selectRange: "Aralık seçin",
            email: "E-posta *",
            password: "Şifre *",
            submit: "Ücretsiz Hesabı Oluştur",
            submitting: "Gönderiliyor…",
            review: "Kayıt sonrası panel erişimi anında aktif edilir.",
            phName: "Ahmet Yılmaz", phCompany: "Marka A.Ş.", phSite: "marka.com", phEmail: "siz@marka.com", phPass: "En az 6 karakter",
        },
    };
    const c = copy[language] || copy.en;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email || !form.password) {
            toast.error(c.fillRequired);
            return;
        }
        if (form.password.length < 6) {
            toast.error(c.passMin);
            return;
        }
        setSubmitting(true);
        const clickIds = getClickIdsFromContext();
        const payload = {
            ...form,
            gclid: clickIds.gclid || undefined,
            fbclid: clickIds.fbclid || undefined,
            ga_client_id: getGaClientId() || undefined,
        };

        const res = await register(payload);
        setSubmitting(false);
        if (res.ok) {
            toast.success(({ tr: "Kayıt tamamlandı. Panelinize yönlendiriliyorsunuz.", es: "Registro completado. Redirigiendo a tu panel.", pt: "Cadastro concluído. Redirecionando para o seu painel.", ru: "Регистрация завершена. Перенаправляем в панель." })[language] || "Registration complete. Redirecting to your panel.");
            trackGa4SignUp("email_application");
            if (res.user?.role === "admin") {
                navigate("/admin");
            } else {
                navigate("/panel");
            }
        } else {
            toast.error(res.error || c.failed);
        }
    };

    return (
        <div data-testid="signup-page" className="min-h-screen grid lg:grid-cols-2">
            <div className="relative hidden lg:block overflow-hidden bg-card border-r border-white/5">
                <div className="absolute inset-0 grid-bg opacity-50" />
                <div className="absolute inset-0 noise" />
                <div
                    aria-hidden
                    className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-primary/[0.04] blur-[120px]"
                />
                <div className="relative h-full flex flex-col justify-between p-12">
                    <Link to="/" data-testid="signup-back-home" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm">
                        <ArrowLeft className="w-4 h-4" /> {c.back}
                    </Link>
                    <div className="space-y-6">
                        <SkalaLogo size="lg" />
                        <h2 className="font-display font-bold text-4xl leading-tight tracking-tight max-w-md">
                            {c.heroTitle1} <br />
                            <span className="text-gradient-primary">{c.heroTitle2}</span>
                        </h2>
                        <ul className="space-y-3 text-foreground/85 text-sm max-w-sm">
                            {c.heroBullets.map((item) => <li key={item}>· {item}</li>)}
                        </ul>
                    </div>
                    <p className="text-xs font-mono text-muted-foreground tracking-wider">
                        {c.heroFoot}
                    </p>
                </div>
            </div>

            <div className="flex items-center justify-center p-6 md:p-12">
                <div className="w-full max-w-md space-y-8">
                    <div className="lg:hidden">
                        <SkalaLogo />
                    </div>
                    <div className="space-y-2">
                        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
                            {c.title}
                        </h1>
                        <p className="text-muted-foreground">
                            {c.approved}{" "}
                            <Link
                                to="/login"
                                data-testid="signup-go-to-login"
                                className="text-primary hover:underline"
                            >
                                {c.login}
                            </Link>
                        </p>
                    </div>

                    <form
                        onSubmit={onSubmit}
                        className="space-y-4"
                        data-testid="signup-form"
                    >
                        <div className="space-y-1.5">
                            <Label htmlFor="full_name">{c.fullName}</Label>
                            <Input
                                id="full_name"
                                data-testid="signup-full-name"
                                value={form.full_name}
                                onChange={(e) => update("full_name", e.target.value)}
                                className="bg-card border-white/10 h-11"
                                placeholder={c.phName}
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{c.monthly}</Label>
                            <Select
                                value={form.monthly_spend}
                                onValueChange={(v) => update("monthly_spend", v)}
                            >
                                <SelectTrigger
                                    data-testid="signup-monthly-spend"
                                    className="bg-card border-white/10 h-11"
                                >
                                    <SelectValue placeholder={c.selectRange} />
                                </SelectTrigger>
                                <SelectContent>
                                    {c.spendOptions.map((s) => (
                                        <SelectItem key={s} value={s}>
                                            {s}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="email">{c.email}</Label>
                            <Input
                                id="email"
                                data-testid="signup-email"
                                type="email"
                                value={form.email}
                                onChange={(e) => update("email", e.target.value)}
                                className="bg-card border-white/10 h-11"
                                placeholder={c.phEmail}
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="password">{c.password}</Label>
                            <Input
                                id="password"
                                data-testid="signup-password"
                                type="password"
                                value={form.password}
                                onChange={(e) => update("password", e.target.value)}
                                className="bg-card border-white/10 h-11"
                                placeholder={c.phPass}
                                minLength={6}
                                required
                            />
                        </div>

                        <Button
                            type="submit"
                            disabled={submitting}
                            data-testid="signup-submit"
                            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11"
                        >
                            {submitting ? c.submitting : c.submit}
                        </Button>

                        <p className="text-xs text-muted-foreground text-center">
                            {c.review}
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
}
